import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { supabase } from "../../../supabaseClient.jsx";
import { useCart } from "../../../shared/contexts/CartContext.jsx";
import { hasActiveSubscription } from "./subscriptionStatus.js";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);

function parseDescricao(descricao) {
  if (!descricao) return "Assinatura mensal para liberar compras de pacotes.";
  if (typeof descricao === "string") return descricao;
  if (typeof descricao === "object") {
    return descricao.texto || descricao.chamada || descricao.ideal_para || "Assinatura mensal para liberar compras de pacotes.";
  }
  return "Assinatura mensal para liberar compras de pacotes.";
}

export default function EscolherAssinaturaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addItem } = useCart();

  const [loading, setLoading] = useState(true);
  const [planos, setPlanos] = useState([]);
  const [error, setError] = useState("");

  const whatsappNumber = import.meta.env.VITE_WHATSAPP_NUMBER;
  const nextPath = searchParams.get("next") || "/dashboard-familia";

  useEffect(() => {
    async function bootstrap() {
      setLoading(true);
      setError("");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const ativa = await hasActiveSubscription({
        userId: user.id,
        email: user.email,
      });

      if (ativa) {
        navigate(nextPath, { replace: true });
        return;
      }

      const { data, error: fetchError } = await supabase
        .from("produtos")
        .select("id, nome, valor, descricao")
        .eq("tipo", "assinatura")
        .eq("ativo", true)
        .order("valor", { ascending: true });

      if (fetchError) {
        setError("Nao foi possivel carregar os planos de assinatura.");
        setPlanos([]);
      } else {
        setPlanos(data || []);
      }

      setLoading(false);
    }

    bootstrap();
  }, [navigate, nextPath]);

  const handleAssinar = (plano) => {
    addItem({
      id: plano.id,
      tipo: "assinatura",
      nome: plano.nome,
      preco: plano.valor || 0,
      valor: plano.valor || 0,
      descricao: plano.descricao,
    });

    navigate("/pagamento", { replace: true });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Etapa obrigatoria
      </p>
      <h1 className="text-2xl font-bold text-foreground">Escolher assinatura antes de comprar pacotes</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Para liberar a compra de pacotes de horas, sua familia precisa ter uma assinatura ativa.
      </p>

      <Card className="mt-5 border-primary/20 bg-primary/5 p-4">
        <div className="flex items-start gap-3 text-sm">
          <ShieldCheck className="mt-0.5 text-primary" size={18} />
          <p className="text-foreground">
            Depois que a assinatura for confirmada, a compra de pacotes fica liberada automaticamente.
          </p>
        </div>
      </Card>

      {loading && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, idx) => (
            <Card key={idx} className="p-5">
              <div className="h-5 w-40 animate-pulse rounded bg-muted" />
              <div className="mt-3 h-4 w-full animate-pulse rounded bg-muted" />
              <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-muted" />
              <div className="mt-5 h-10 w-full animate-pulse rounded bg-muted" />
            </Card>
          ))}
        </div>
      )}

      {!loading && !!error && (
        <Card className="mt-6 border-destructive/40 bg-destructive/5 p-5">
          <p className="text-sm font-medium text-destructive">{error}</p>
        </Card>
      )}

      {!loading && !error && planos.length === 0 && (
        <Card className="mt-6 p-5">
          <p className="text-sm text-muted-foreground">Nao ha assinaturas ativas no momento.</p>
        </Card>
      )}

      {!loading && planos.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {planos.map((plano) => (
            <Card key={plano.id} className="p-5">
              <h2 className="text-lg font-semibold text-foreground">{plano.nome}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{parseDescricao(plano.descricao)}</p>
              <p className="mt-4 text-2xl font-bold text-foreground">{formatCurrency(plano.valor || 0)}<span className="ml-1 text-sm font-medium text-muted-foreground">/mes</span></p>
              <Button className="mt-5 w-full" onClick={() => handleAssinar(plano)}>
                Assinar agora
              </Button>
            </Card>
          ))}
        </div>
      )}

      {whatsappNumber && (
        <Card className="mt-6 p-5">
          <h3 className="text-base font-semibold text-foreground">Suporte - Acesso ao WhatsApp</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Precisa de ajuda para escolher a assinatura? Fale com nosso suporte.
          </p>
          <Button variant="outline" className="mt-4 gap-2" asChild>
            <a
              href={`https://wa.me/${whatsappNumber}?text=Ola, preciso de ajuda para escolher a assinatura da minha familia.`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={16} />
              Falar com suporte
            </a>
          </Button>
        </Card>
      )}
    </div>
  );
}
